import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Receipt, ArrowUpRight, ArrowDownRight, Building2, RefreshCw, 
  Calendar, CheckCircle, AlertTriangle, Wallet, Clock, CalendarClock,
  FileText, Plus, Pencil, Trash2, PiggyBank
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VATSummary() {
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [vatData, setVatData] = useState(null);
  
  // Arrangement dialog state
  const [showArrangementDialog, setShowArrangementDialog] = useState(false);
  const [arrangementPeriod, setArrangementPeriod] = useState('');
  const [arrangementAmount, setArrangementAmount] = useState('');
  const [arrangementNotes, setArrangementNotes] = useState('');
  const [savingArrangement, setSavingArrangement] = useState(false);
  const [editingArrangement, setEditingArrangement] = useState(null);

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  const openArrangementDialog = (period, existingArrangement = null) => {
    setArrangementPeriod(period);
    if (existingArrangement) {
      setArrangementAmount(existingArrangement.amount?.toString() || '');
      setArrangementNotes(existingArrangement.notes || '');
      setEditingArrangement(existingArrangement);
    } else {
      setArrangementAmount('');
      setArrangementNotes('');
      setEditingArrangement(null);
    }
    setShowArrangementDialog(true);
  };

  const saveArrangement = async () => {
    if (!arrangementAmount || !arrangementPeriod) {
      toast.error('Please enter an arrangement amount');
      return;
    }
    setSavingArrangement(true);
    try {
      await axios.post(`${API_URL}/finance/sars-arrangements`, {
        period: arrangementPeriod,
        financial_year: selectedYear,
        arrangement_amount: parseFloat(arrangementAmount),
        notes: arrangementNotes
      });
      toast.success('Arrangement saved successfully');
      setShowArrangementDialog(false);
      fetchVATSummary(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save arrangement'));
    } finally {
      setSavingArrangement(false);
    }
  };

  const deleteArrangement = async (period) => {
    if (!confirm('Are you sure you want to delete this arrangement?')) return;
    try {
      await axios.delete(`${API_URL}/finance/sars-arrangements/${encodeURIComponent(period)}/${encodeURIComponent(selectedYear)}`);
      toast.success('Arrangement deleted');
      fetchVATSummary(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete arrangement'));
    }
  };

  useEffect(() => {
    if (selectedYear) {
      fetchVATSummary(selectedYear);
    }
  }, [selectedYear]);

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/years`);
      const years = response.data.financial_years || [];
      setFinancialYears(years);
      if (years.length > 0) {
        setSelectedYear(years[0]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load financial years'));
    }
  };

  const fetchVATSummary = async (year) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/vat-summary`, {
        params: { financial_year: year }
      });
      setVatData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load VAT summary'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Paid</Badge>;
      case 'partial':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" /> Partial</Badge>;
      case 'outstanding':
        return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" /> Outstanding</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'arrangement':
        return <Badge className="bg-orange-100 text-orange-700"><FileText className="h-3 w-3 mr-1" /> Arrangement</Badge>;
      case 'refund':
        return <Badge className="bg-purple-100 text-purple-700"><ArrowDownRight className="h-3 w-3 mr-1" /> Credit</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vat-summary-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">VAT Summary</h1>
            <p className="text-muted-foreground mt-1">
              Category B (Bi-monthly) VAT cycle tracking
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-44">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {financialYears.map(fy => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => fetchVATSummary(selectedYear)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : vatData ? (
          <>
            {/* Next Due Alert */}
            {vatData.next_due?.period && (
              <Alert className="bg-blue-50 border-blue-300">
                <CalendarClock className="h-5 w-5 text-blue-600" />
                <AlertTitle className="text-blue-800 font-bold">Next VAT Payment Due</AlertTitle>
                <AlertDescription className="text-blue-700">
                  <strong>{vatData.next_due.period}</strong> VAT period is due by <strong>{vatData.next_due.due_date}</strong>
                  {vatData.current_period && (
                    <span className="ml-2 text-sm">(Currently in {vatData.current_period} period)</span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* VAT Status Alert */}
            {vatData.summary.status === 'owing' ? (
              <Alert variant="destructive" className="bg-red-50 border-red-500">
                <AlertTriangle className="h-5 w-5" />
                <AlertTitle className="text-red-800 font-bold">VAT Outstanding</AlertTitle>
                <AlertDescription className="text-red-700">
                  Total outstanding VAT: <strong>{formatCurrency(vatData.summary.amount)}</strong>
                </AlertDescription>
              </Alert>
            ) : vatData.summary.status === 'refundable' && (
              <Alert className="bg-green-50 border-green-500">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <AlertTitle className="text-green-800 font-bold">VAT Overpaid</AlertTitle>
                <AlertDescription className="text-green-700">
                  You have overpaid VAT by <strong>{formatCurrency(vatData.summary.amount)}</strong>. This is refundable from SARS.
                </AlertDescription>
              </Alert>
            )}

            {/* VAT Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <ArrowUpRight className="h-8 w-8 text-red-600" />
                    <div>
                      <p className="text-sm text-red-700">Output VAT (from Income)</p>
                      <p className="text-2xl font-bold text-red-700">{formatCurrency(vatData.output_vat)}</p>
                      <p className="text-xs text-red-600">VAT collected on sales</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-8 w-8 text-purple-600" />
                    <div>
                      <p className="text-sm text-purple-700">Paid to SARS</p>
                      <p className="text-2xl font-bold text-purple-700">{formatCurrency(vatData.vat_paid_to_sars)}</p>
                      <p className="text-xs text-purple-600">Total payments made</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`${vatData.vat_balance > 0 ? 'bg-red-100 border-red-300' : 'bg-green-100 border-green-300'}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Receipt className="h-8 w-8" />
                    <div>
                      <p className="text-sm">Outstanding Balance</p>
                      <p className={`text-2xl font-bold ${vatData.vat_balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {formatCurrency(Math.abs(vatData.vat_balance))}
                      </p>
                      <p className="text-xs">{vatData.vat_balance > 0 ? 'Still owing to SARS' : 'Overpaid / Credit'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* VAT Calculation Breakdown */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  VAT Calculation
                </CardTitle>
                <CardDescription>Step-by-step VAT liability calculation</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="font-medium">Output VAT (from Income)</span>
                    <span className="text-lg font-bold text-red-700">{formatCurrency(vatData.output_vat)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-muted-foreground">Less: VAT Paid to SARS</span>
                    <span className="text-lg font-medium text-purple-700">- {formatCurrency(vatData.vat_paid_to_sars)}</span>
                  </div>
                  <div className={`flex items-center justify-between py-3 px-3 rounded-lg ${vatData.vat_balance > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                    <span className="font-bold text-lg">= Balance</span>
                    <div className="text-right">
                      <span className={`text-2xl font-bold ${vatData.vat_balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {formatCurrency(Math.abs(vatData.vat_balance))}
                      </span>
                      <Badge className={`ml-2 ${vatData.vat_balance > 0 ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>
                        {vatData.vat_balance > 0 ? 'STILL OWING' : 'OVERPAID / REFUNDABLE'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Current Period VAT & What's Actually Due Now */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Current Period Card */}
              <Card className="border-2 border-blue-300 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Clock className="h-5 w-5" />
                    Current Period VAT (Pending)
                  </CardTitle>
                  <CardDescription>
                    {vatData.current_period} - Not yet due
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-700">
                      {formatCurrency(vatData.periods?.find(p => p.status === 'pending')?.net_vat_due || 0)}
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      Due: {vatData.periods?.find(p => p.status === 'pending')?.due_date}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Actually Due Now Card */}
              <Card className="border-2 border-red-300 bg-red-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-5 w-5" />
                    Actually Due NOW
                  </CardTitle>
                  <CardDescription>Excluding current pending period</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Total Outstanding (after pool)</span>
                      <span className="font-medium">{formatCurrency(vatData.total_outstanding_after_pool)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-blue-700">
                      <span>Less: Current Period (Pending)</span>
                      <span className="font-medium">- {formatCurrency(vatData.periods?.find(p => p.status === 'pending')?.balance || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-red-300">
                      <span className="font-bold text-red-800">= ACTUALLY DUE NOW</span>
                      <span className="text-2xl font-bold text-red-700">
                        {formatCurrency(
                          (vatData.total_outstanding_after_pool || 0) - 
                          (vatData.periods?.find(p => p.status === 'pending')?.balance || 0)
                        )}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* VAT Pool Card */}
            {(vatData.vat_pool > 0 || vatData.total_arrangements > 0) && (
              <Card className="border-2 border-purple-300 bg-purple-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-purple-800">
                    <PiggyBank className="h-5 w-5" />
                    VAT Credit Pool & Arrangements
                  </CardTitle>
                  <CardDescription>Credits and arrangements that reduce your outstanding VAT</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-purple-600">Credits from Overpayments</p>
                      <p className="text-xl font-bold text-purple-700">{formatCurrency(vatData.vat_pool)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-orange-600">SARS Arrangements</p>
                      <p className="text-xl font-bold text-orange-700">{formatCurrency(vatData.total_arrangements)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-green-600">Total Applied</p>
                      <p className="text-xl font-bold text-green-700">
                        {formatCurrency((vatData.vat_pool - vatData.vat_pool_remaining) + vatData.total_arrangements_applied)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-red-600">Still Outstanding</p>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(vatData.total_outstanding_after_pool)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bi-Monthly VAT Periods */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarClock className="h-5 w-5" />
                  Bi-Monthly VAT Periods
                </CardTitle>
                <CardDescription>
                  Category B VAT cycle - Payment due every 2 months
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {vatData.periods && vatData.periods.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Output VAT</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right text-cyan-700">Pool Applied</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="text-right">Arrangement</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vatData.periods.map((period, idx) => (
                        <React.Fragment key={idx}>
                          <TableRow className={
                            period.status === 'outstanding' ? 'bg-red-50' : 
                            period.status === 'pending' ? 'bg-blue-50' :
                            period.status === 'paid' ? 'bg-green-50/50' : ''
                          }>
                            <TableCell className="font-medium">
                              {period.period}
                              {period.status === 'pending' && <span className="text-xs text-blue-600 ml-1">(current)</span>}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{period.due_date}</TableCell>
                            <TableCell className="text-right text-red-700 font-medium">{formatCurrency(period.output_vat)}</TableCell>
                            <TableCell className="text-right text-purple-700">{formatCurrency(period.amount_paid)}</TableCell>
                            <TableCell className="text-right">
                              {period.pool_applied > 0 ? (
                                <span className="text-cyan-700 font-medium">{formatCurrency(period.pool_applied)}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className={`text-right font-bold ${period.balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                              {formatCurrency(Math.abs(period.balance))}
                              {period.balance < 0 && <span className="text-xs ml-1">(credit)</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              {period.arrangement ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span className="text-sm text-orange-700 font-medium">
                                    {formatCurrency(period.arrangement.amount)}
                                  </span>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openArrangementDialog(period.period, period.arrangement)}>
                                    <Pencil className="h-3 w-3 text-gray-500" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => deleteArrangement(period.period)}>
                                    <Trash2 className="h-3 w-3 text-red-500" />
                                  </Button>
                                </div>
                              ) : (
                                <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => openArrangementDialog(period.period)}>
                                  <Plus className="h-3 w-3 mr-1" /> Add
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>{getStatusBadge(period.status)}</TableCell>
                          </TableRow>
                          {/* Show payments for this period */}
                          {period.payments && period.payments.length > 0 && (
                            <TableRow className="bg-purple-50/50">
                              <TableCell colSpan={8} className="py-1 px-4">
                                <div className="text-xs text-purple-700">
                                  <span className="font-semibold">Payments:</span>
                                  {period.payments.map((p, pIdx) => (
                                    <span key={pIdx} className="ml-2">
                                      {p.date}: {formatCurrency(p.amount)}
                                      {pIdx < period.payments.length - 1 && ' |'}
                                    </span>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No VAT period data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* VAT Payments History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  VAT Payments History
                </CardTitle>
                <CardDescription>
                  Payments made to South African Revenue Service
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {vatData.vat_payments && vatData.vat_payments.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vatData.vat_payments.map((payment, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{payment.date}</TableCell>
                          <TableCell className="text-muted-foreground max-w-[300px] truncate" title={payment.reference}>
                            {payment.reference || payment.description}
                          </TableCell>
                          <TableCell className="text-right font-medium text-purple-700">
                            {formatCurrency(payment.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No VAT payments recorded
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            Select a financial year to view VAT summary
          </div>
        )}
      </div>

      {/* SARS Arrangement Dialog */}
      <Dialog open={showArrangementDialog} onOpenChange={setShowArrangementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingArrangement ? 'Edit' : 'Add'} SARS Arrangement
            </DialogTitle>
            <DialogDescription>
              Record a payment arrangement with SARS for the <strong>{arrangementPeriod}</strong> VAT period.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="arrangement-amount">Arrangement Amount (R)</Label>
              <Input
                id="arrangement-amount"
                type="number"
                step="0.01"
                placeholder="Enter amount"
                value={arrangementAmount}
                onChange={(e) => setArrangementAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arrangement-notes">Notes (optional)</Label>
              <Input
                id="arrangement-notes"
                placeholder="e.g., Monthly payment plan, Reference number"
                value={arrangementNotes}
                onChange={(e) => setArrangementNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArrangementDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveArrangement} disabled={savingArrangement}>
              {savingArrangement ? 'Saving...' : 'Save Arrangement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
